import React from 'react';
import {
  DetailsContainer,
  Title,
  Details,
  Input,
  Label,
  Summary,
  Ul,
  Li,
} from './styles';
import {
  ErrorMessage,
  FormController,
} from '../../../assets/global/globalStyles';
import { useField } from '@unform/core';
import useDebounce from '../../../hooks/useDebounce';

const UlValues = React.forwardRef(({ children, error }, forwardedRef) => {
  const localInputRef = React.useRef();
  React.useImperativeHandle(forwardedRef, () => {
    return {
      valueSelect: () => {
        const temp = [];
        Array.from(localInputRef.current.children).forEach((li) => {
          const input = li.children[0];
          if (input && input.checked) temp.push(input.value);
        });
        return temp;
      },
    };
  });

  return (
    <Ul error={error} ref={localInputRef}>
      {children}
    </Ul>
  );
});

/**
 * @param props
 * @param {String} props.name
 * @param {String} props.label
 * @param {Array<Object>} props.options
 * @param {String} props.grid
 * @param {String} props.gridRow
 * @param {Object} props.obj
 */
function MultipleSelect({ label, name, options, grid, gridRow, defaultValue}) {
  const refValue = React.useRef();
  const { fieldName, registerField, error } = useField(name);
  const [history, setHistory] = React.useState(
    !!defaultValue ? defaultValue : [],
  );
  const debouncedChange = useDebounce((value) => {
    setFilter(value);
  }, 500);
  const [filter, setFilter] = React.useState('');
  const [filterTemp, setFilterTemp] = React.useState('');
  const filterSelect = ({ target: { value } }) => {
    setFilterTemp(value);
    debouncedChange(value);
  };

  const onCheck = (checked, item) => {
    setHistory((prev) =>
      checked ? [...prev, item] : prev.filter((value) => value !== item),
    );
  };

  React.useEffect(() => {
    registerField({
      name: fieldName,
      ref: refValue?.current,
      getValue: () => history,
    });
  }, [registerField, fieldName, history]);

  const itemSelect = () => {
    const temp = [];
    options.forEach(({ value, label }) => {
      history.forEach((item) => {
        if (item === value) temp.push(label);
      });
    });
    return temp;
  };

  const functionOrder = ({ value: valueA }, { value: valueB }) =>
    history.includes(valueB) - history.includes(valueA);

  const functionFilter = ({ label }) => {
    const noAccent = label
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    const withAccent = label.toLowerCase();

    return (
      noAccent.includes(filter.trim().toLowerCase()) ||
      withAccent.includes(filter.trim().toLowerCase())
    );
  };

  return (
    <FormController grid={grid} gridRow={gridRow}>
      <DetailsContainer>
        <Title error={error}>{label}</Title>
        <Details>
          <Summary error={error}>
            {!!history.length ? (
              history.length <= 3 ? (
                itemSelect().join(', ')
              ) : (
                `${history.length} items`
              )
            ) : (
              <p>&nbsp;</p>
            )}
          </Summary>
          <UlValues error={error} ref={refValue}>
            <Input
              type="text"
              placeholder="Pesquisar..."
              onChange={filterSelect}
              value={filterTemp}
            />
            {options
              .sort(functionOrder)
              .filter(functionFilter)
              .map(({ label, value }) => (
                <Li key={value}>
                  <Input
                    id={label}
                    type="checkbox"
                    value={value}
                    onChange={({ target: { checked } }) =>
                      onCheck(checked, value)
                    }
                    checked={history.includes(value)}
                    defaultChecked={
                      !!defaultValue && defaultValue?.includes(value)
                    }
                  />
                  <Label htmlFor={label}>{label}</Label>
                </Li>
              ))}
          </UlValues>
          {error && <ErrorMessage>{error}</ErrorMessage>}
        </Details>
      </DetailsContainer>
    </FormController>
  );
}

export default React.memo(MultipleSelect);
